import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Box,
  Button,
  Center,
  CloseButton,
  SimpleGrid,
  Select,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
  Title,
} from '@mantine/core';
import { IconAdjustments, IconSearch, IconUsersGroup, IconAlertCircle, IconFilterOff } from '@tabler/icons-react';
import { UserCircle } from '@risrx/icons';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { clearSessionExpiry, logoutSession } from '../api/auth';
import { fetchResultsList, fetchResultsEnrichment, type EscalationListStatus, type ResultOutcome, type SubmissionResultRecord } from '../api/result';
import { ApiError } from '../api/http';
import PageShell from '../components/PageShell';
import { useProtectedSession } from '../hooks/useProtectedSession';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import { useCallback, useEffect, useState } from 'react';
import { buildReturnToPath } from '../utils/portalPath';
import {
  PORTAL_ESCALATION_STEPS,
  getStatusPresentation,
} from '../utils/escalationStatus';
import NotFoundPage from './NotFoundPage';

const PAGE_SIZE = 20;

const OUTCOME_LABELS: Record<ResultOutcome, string> = {
  success: 'Pending',
  already_enrolled: 'Already Enrolled',
  rejected: 'Rejected',
  validation_error: 'Validation Error',
  ineligible: 'Ineligible',
  system_error: 'System Error',
  pending: 'Pending',
};

const OUTCOME_COLORS: Record<ResultOutcome, string> = {
  success: 'gray',
  already_enrolled: 'green',
  rejected: 'red',
  validation_error: 'yellow',
  ineligible: 'yellow',
  system_error: 'red',
  pending: 'blue',
};

function getStatusRow(record: SubmissionResultRecord, status?: EscalationListStatus): { label: string; color: string } {
  if (status?.step) {
    return getStatusPresentation(status);
  }

  const outcome = record.outcome as ResultOutcome;
  return {
    label: OUTCOME_LABELS[outcome] ?? outcome,
    color: OUTCOME_COLORS[outcome] ?? 'gray',
  };
}

export default function EscalationHistoryPage() {
  const { data: config, isLoading: configLoading, error: configError, resolvedProgram, sessionScopeKey } = useResolvedProgram();
  const sessionQuery = useProtectedSession(sessionScopeKey);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const offset = Number(searchParams.get('offset')) || 0;
  const search = searchParams.get('q') ?? '';
  const searchKeywords = search.split(',').map((keyword) => keyword.trim()).filter(Boolean);
  const statusFilter = searchParams.get('status') ?? 'All';
  const priorityFilter = searchParams.get('priority') ?? 'All';
  const viewMine = searchParams.get('viewMine') === 'true'; // Default to false (view all)
  const [draftKeyword, setDraftKeyword] = useState('');
  const [debouncedDraftKeyword, setDebouncedDraftKeyword] = useState('');
  const [filterField, setFilterField] = useState('reporter_name');
  const [filterValue, setFilterValue] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedDraftKeyword(draftKeyword);
    }, 275);

    return () => window.clearTimeout(timeoutId);
  }, [draftKeyword]);

  const liveSearch = [...searchKeywords, debouncedDraftKeyword.trim()].filter(Boolean).join(', ');

  const filterLabels = Object.fromEntries(
    (config?.escalations?.pages.history?.filters ?? []).map((filter) => [filter.field, filter.label]),
  );
  const filterOptions = config?.escalations?.pages.history?.filters ?? [];

  const activeColumnFilters = Array.from(searchParams.entries())
    .filter(([key, value]) => key.startsWith('filter.') && value.trim() !== '' && filterOptions.some((filter) => filter.field === key.slice('filter.'.length)))
    .reduce<Record<string, string[]>>((filters, [key, value]) => {
      filters[key.slice('filter.'.length)] = [value];
      return filters;
    }, {});
  const hasActiveColumnFilters = Object.keys(activeColumnFilters).length > 0;
  const addColumnFilter = () => {
    const value = filterValue.trim();
    if (!value) return;
    updateParams({ [`filter.${filterField}`]: value, offset: 0 });
    setFilterValue('');
  };

  const clearColumnFilters = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const key of Array.from(next.keys())) {
        if (key.startsWith('filter.')) next.delete(key);
      }
      next.delete('offset');
      return next;
    }, { replace: true });
  };

  const updateParams = useCallback((updates: Record<string, string | number | boolean | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '' || value === 0 || value === false ||
            (key === 'status' && value === 'All')) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const addSearchKeyword = (value: string) => {
    const keyword = value.trim();
    if (!keyword || searchKeywords.some((existing) => existing.toLowerCase() === keyword.toLowerCase())) {
      setDraftKeyword('');
      return;
    }
    updateParams({ q: [...searchKeywords, keyword].join(', '), offset: 0 });
    setDraftKeyword('');
  };

  const removeSearchKeyword = (keywordToRemove: string) => {
    updateParams({
      q: searchKeywords.filter((keyword) => keyword !== keywordToRemove).join(', '),
      offset: 0,
    });
  };

  const clearSearch = () => {
    setDraftKeyword('');
    updateParams({ q: null, offset: 0 });
  };

  const isEscalationIQ = config?.solution === 'escalationiq';

  const isSuperuser = sessionQuery.data?.role === 'superuser';
  const sessionEmail = sessionQuery.data?.email;
  const canViewAll = isSuperuser || Boolean(config?.escalations?.capabilities?.viewAllEnabled);

  const logoutMutation = useMutation({ mutationFn: logoutSession });

  const historyQuery = useQuery({
    queryKey: ['results-list', offset, resolvedProgram?.clientName, resolvedProgram?.programName, viewMine, liveSearch, statusFilter, priorityFilter, activeColumnFilters],
    queryFn: () => {
      return fetchResultsList({
        limit: PAGE_SIZE,
        offset,
        viewMine,
        search: liveSearch || undefined,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        columnFilters: {
          ...(priorityFilter !== 'All' ? { priority: [priorityFilter] } : {}),
          ...activeColumnFilters,
        },
      });
    },
    enabled: Boolean(config && isEscalationIQ && !sessionQuery.isUnauthorized),
    retry: false,
    refetchOnMount: 'always',
    placeholderData: (prev) => prev,
  });

  // Second query: enrich visible result IDs with current row data.
  // Mock/demo responses already contain complete row data and skip this request.
  const resultIds = historyQuery.data?.results?.map(r => r.resultId);
  const needsEnrichment = historyQuery.data?.needsEnrichment === true;

  const enrichmentQuery = useQuery({
    queryKey: ['results-enrichment', resultIds, viewMine],
    queryFn: () => fetchResultsEnrichment(resultIds ?? [], viewMine),
    enabled: Boolean(resultIds?.length) && needsEnrichment,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      if (sessionScopeKey) clearSessionExpiry(sessionScopeKey);
      navigate('/auth/request', { replace: true });
    }
  };

  if (configLoading || sessionQuery.isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (sessionQuery.isUnauthorized) {
    return <Navigate to="/auth/request" state={{ returnTo: buildReturnToPath(location) }} replace />;
  }

  if (configError || !config) {
    return <Navigate to="/" replace />;
  }

  if (!isEscalationIQ) {
    return <NotFoundPage />;
  }

  const list = historyQuery.data;
  const total = list?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const results = list?.results ?? [];

  // Merge: start with DB-persisted steps, override with fresh BenIQ data.
  const enrichmentData = enrichmentQuery.isFetching ? {} : (enrichmentQuery.data ?? {});
  const escalationStatuses: Record<string, EscalationListStatus> = {
    ...(list?.escalationStatuses ?? {}),
    ...enrichmentData,
  };
  const historyColumns = list?.historyColumns ?? [];
  const stepCounts = list?.stepCounts ?? {};

  // Derive summary card totals from stepCounts — covers all rows in the DB,
  // not just the current page.
  const counts = {
    total: Object.values(stepCounts).reduce((s, n) => s + n, 0),
    awaiting: (stepCounts['Escalation Submitted'] ?? 0) + (stepCounts['Agent Assigned'] ?? 0),
    inProgress: stepCounts['In Progress'] ?? 0,
    closed: stepCounts['Closed'] ?? 0,
  };

  return (
    <PageShell
      config={config}
      containerSize="lg"
      hideTitle
      sessionEmail={sessionEmail}
      onGoHome={() => navigate('/')}
      onLogout={() => { void handleLogout(); }}
      isLoggingOut={logoutMutation.isPending}
    >
      <Stack gap="md">
            <Group justify="space-between" align="flex-end">
              <div>
                <Title order={3} className="portal-section-title" size="2rem" style={ {paddingBottom:"0.5rem"}}>
                   Track escalations
                </Title>
                <Text size="sm" c="dimmed">
                  {canViewAll ? (viewMine ? 'Showing only your escalations' : 'Showing all escalations') : 'Showing only your escalations'}
                </Text>
              </div>
              <Group gap="sm" align="center">
                {canViewAll && (
                  <Button
                    size="xs"
                    variant={viewMine ? 'filled' : 'outline'}
                    color={viewMine ? 'teal' : 'gray'}
                    // leftSection={viewAll ? <IconUsersGroup size={20} /> : <UserCircle size={16} />}
                    leftSection={viewMine ? <UserCircle size={16} /> : <IconUsersGroup size={20} />}
                    onClick={() => { updateParams({ viewMine: !viewMine, offset: 0 }); }}
                  >
                    {viewMine ? 'Viewing mine' : 'View mine'}
                  </Button>
                )}
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => navigate('/escalations')}
                  style={{ color: '#FFFFFF', borderColor: '#00A57A', backgroundColor: '#00A57A' }}
                >
                  New escalation
                </Button>
              </Group>
            </Group>

            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
              <Paper p="md" withBorder radius="md" className="portal-panel portal-panel-muted">
                <Text className="portal-kicker">Total</Text>
                <Title order={3} className="portal-section-title">{counts.total}</Title>
              </Paper>
              <Paper p="md" withBorder radius="md" className="portal-panel portal-panel-muted">
                <Text className="portal-kicker">Awaiting Response</Text>
                <Title order={3} className="portal-section-title">{counts.awaiting}</Title>
              </Paper>
              <Paper p="md" withBorder radius="md" className="portal-panel portal-panel-muted">
                <Text className="portal-kicker">In Progress</Text>
                <Title order={3} className="portal-section-title">{counts.inProgress}</Title>
              </Paper>
              <Paper p="md" withBorder radius="md" className="portal-panel portal-panel-muted">
                <Text className="portal-kicker">Closed</Text>
                <Title order={3} className="portal-section-title">{counts.closed}</Title>
              </Paper>
            </SimpleGrid>

            <Paper p="md" withBorder radius="md" className="portal-panel">
              <Group
                gap="sm"
                align={hasActiveColumnFilters ? 'stretch' : 'center'}
                wrap={hasActiveColumnFilters ? undefined : 'wrap'}
                style={hasActiveColumnFilters ? { flexDirection: 'column' } : undefined}
              >
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    flexWrap: 'wrap',
                    flex: hasActiveColumnFilters ? 'none' : '1 1 260px',
                    width: hasActiveColumnFilters ? '100%' : undefined,
                    minWidth: hasActiveColumnFilters ? undefined : 260,
                    minHeight: 36,
                    padding: '3px 8px',
                    border: '1px solid var(--mantine-color-default-border)',
                    borderRadius: 'var(--mantine-radius-sm)',
                  }}
                >
                  {searchKeywords.map((keyword) => (
                    <Badge
                      key={keyword}
                      variant="light"
                      color='gray'
                      rightSection={<CloseButton size="xs" aria-label={`Remove ${keyword}`} onClick={() => removeSearchKeyword(keyword)} />}
                    >
                      {keyword}
                    </Badge>
                  ))}
                  <TextInput
                    placeholder={config.escalations?.pages.history?.searchPlaceholder ?? 'Search escalations'}
                    leftSection={<IconSearch size={15} />}
                    value={draftKeyword}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      const lastComma = value.lastIndexOf(',');
                      if (lastComma >= 0) {
                        addSearchKeyword(value.slice(0, lastComma));
                        setDraftKeyword(value.slice(lastComma + 1));
                        return;
                      }
                      setDraftKeyword(value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSearchKeyword(draftKeyword);
                      } else if (e.key === 'Backspace' && !draftKeyword && searchKeywords.length > 0) {
                        removeSearchKeyword(searchKeywords[searchKeywords.length - 1]);
                      }
                    }}
                    styles={{
                      root: { flex: '1 1 220px', minWidth: 220 },
                      input: { border: 0, paddingRight: 0 },
                    }}
                  />
                  {(draftKeyword || searchKeywords.length > 0) && (
                    <Tooltip label="Clear all search keywords">
                      <Button
                        variant="subtle"
                        color="gray"
                        size="compact-xs"
                        px={6}
                        aria-label="Clear all search keywords"
                        onClick={clearSearch}
                      >
                        <IconFilterOff size={16} />
                      </Button>
                    </Tooltip>
                  )}
                </Box>
                <Group gap={6} wrap="wrap">
                  {filterOptions.length > 0 && (
                    <Button
                      size="xs"
                      variant={filtersOpen ? 'filled' : 'default'}
                      color={config.theme.primaryColor}
                      leftSection={<IconAdjustments size={14} />}
                      onClick={() => setFiltersOpen((open) => !open)}
                    >
                      Filters
                    </Button>
                  )}
                  {['All', ...PORTAL_ESCALATION_STEPS].map((s) => (
                    <Button
                      key={s}
                      size="xs"
                      radius="xl"
                      variant={statusFilter === s ? 'filled' : 'outline'}
                      color="gray"
                      className={statusFilter === s ? 'portal-primary-button' : undefined}
                      onClick={() => {
                        updateParams({ status: s, offset: 0 });
                      }}
                    >
                      {s}
                    </Button>
                  ))}
                  {historyColumns.some(col => col.label === 'Priority') && (
                    <Button
                      size="xs"
                      radius="xl"
                      variant={priorityFilter.trim() === 'URGENT' ? 'filled' : 'outline'}
                      color={priorityFilter.trim() === 'URGENT' ? 'red' : 'gray'}
                      onClick={() => {
                        updateParams({ priority: priorityFilter.trim() === 'URGENT' ? 'All' : 'URGENT', offset: 0 });
                      }}
                    >
                      Urgent
                    </Button>
                  )}
                </Group>
              </Group>
              {hasActiveColumnFilters && (
                <Group gap={6} mt="sm" wrap="wrap">
                  {Object.entries(activeColumnFilters).map(([field, values]) => (
                    <Badge
                      key={field}
                      variant="light"
                      color="gray"
                      rightSection={<CloseButton size="xs" aria-label={`Remove ${filterLabels[field] ?? field} filter`} onClick={() => updateParams({ [`filter.${field}`]: null, offset: 0 })} />}
                    >
                      {filterLabels[field] ?? field} : {values[0]}
                    </Badge>
                  ))}
                  <Button size="xs" variant="subtle" color="gray" onClick={clearColumnFilters}>
                    Clear filters
                  </Button>
                </Group>
              )}
              {filterOptions.length > 0 && filtersOpen && (
                <Group gap="xs" mt="sm" align="flex-end" wrap="wrap">
                  <Select
                    label="Field"
                    aria-label="Filter field"
                    size="xs"
                    value={filterField}
                    data={filterOptions.map((filter) => ({ value: filter.field, label: filter.label }))}
                    onChange={(value) => setFilterField(value ?? 'reporter_name')}
                    styles={{ input: { minWidth: 155 } }}
                  />
                  <TextInput
                    label="Value"
                    aria-label="Filter value"
                    size="xs"
                    placeholder="Enter a value"
                    value={filterValue}
                    onChange={(event) => setFilterValue(event.currentTarget.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addColumnFilter();
                      }
                    }}
                    styles={{ input: { minWidth: 180 } }}
                  />
                  <Button size="xs" variant="default" onClick={addColumnFilter}>Add filter</Button>
                </Group>
              )}
            </Paper>

            <Paper p={0} withBorder radius="md" className="portal-panel">
              <Stack gap="md">
            {historyQuery.isLoading && (
              <Center py="xl">
                <Loader size="sm" />
              </Center>
            )}

            {(historyQuery.isFetching && !historyQuery.isLoading) && (
              <Center py="xs">
                <Loader size="xs" />
              </Center>
            )}

            {enrichmentQuery.isError && !enrichmentQuery.isFetching && (
              <Alert color="yellow" variant="light">
                Live statuses could not be loaded. The data shown is from our last sync. Please try again in a few minutes.
              </Alert>
            )}

            {historyQuery.isError && (
              <Alert color="red" title="Unable to load history">
                {historyQuery.error instanceof ApiError && historyQuery.error.status === 503
                  ? 'History is currently unavailable. Please try again shortly.'
                  : 'An error occurred while loading your submission history.'}
              </Alert>
            )}

            {list && !historyQuery.isError && (
              <>
                <div
                  style={{ position: 'relative' }}
                  aria-busy={enrichmentQuery.isFetching && !historyQuery.isFetching}
                >
                <Table highlightOnHover withTableBorder className="portal-data-table">
                  <Table.Thead>
                    <Table.Tr>
                      {historyColumns.map((col, idx) => (
                        <Table.Th key={idx} ta={col.label === 'Priority' ? 'center' : undefined}>
                          <Text fw={600}>{col.label}</Text>
                        </Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {results.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={historyColumns.length}>
                          <Text size="sm" c="dimmed" ta="center" py="xl">
                            {search || statusFilter !== 'All' || priorityFilter !== 'All' || Object.keys(activeColumnFilters).length > 0 ? 'No escalations match the current search or filter.' : 'No escalations found.'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      results.map((record) => {
                      const status = escalationStatuses[record.resultId];
                      const presentation = getStatusRow(record, status);
                      const statusColumns = status?.columns ?? [];
                      
                      return (
                        <Table.Tr
                          key={record.resultId}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/escalations/history/${record.resultId}`)}
                        >
                          {historyColumns.map((col, colIdx) => {
                            // Status column: render badge
                            if (col.type === 'status') {
                              return (
                                <Table.Td key={colIdx}>
                                  <Badge color={presentation.color} variant="light" size="sm">
                                    {presentation.label}
                                  </Badge>
                                </Table.Td>
                              );
                            }
                            
                            // Data column: get value from statusColumns array (skipping status columns)
                            // statusColumns only has data column values, so we need to map colIdx to data column index
                            const dataColIdx = historyColumns.slice(0, colIdx).filter(c => c.type !== 'status').length;
                            const colData = statusColumns[dataColIdx];
                            let displayValue = colData?.value || '—';
                            
                            // Format date columns
                            if (col.label === 'Submission Date' && colData?.value) {
                              try {
                                const date = new Date(colData.value);
                                if (!isNaN(date.getTime())) {
                                  displayValue = date.toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  });
                                }
                              } catch {
                                // Keep original value if parsing fails
                              }
                            }
                            
                            // Special handling for Priority column
                            if (col.label === 'Priority') {
                              return (
                                <Table.Td key={colIdx} ta="center">
                                  {colData?.value?.trim() === 'URGENT' ? (
                                    <IconAlertCircle size={16} color="var(--mantine-color-red-6)" aria-label="urgent" />
                                  ) : colData?.value?.trim() === 'STANDARD' ? null : (
                                    <Text size="sm">{displayValue}</Text>
                                  )}
                                </Table.Td>
                              );
                            }
                            
                            return (
                              <Table.Td key={colIdx}>
                                <Text size="sm">{displayValue}</Text>
                              </Table.Td>
                            );
                          })}
                        </Table.Tr>
                      );
                    }))}
                  </Table.Tbody>
                </Table>
                {enrichmentQuery.isFetching && !historyQuery.isFetching && (
                  <div
                    role="status"
                    aria-live="polite"
                    aria-label="Refreshing statuses"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  >
                    <Loader size="sm" color="gray" />
                  </div>
                )}
                </div>

                <Group justify="space-between" align="center" px="md" pb="md">
                  <Button
                    variant="default"
                    size="xs"
                    disabled={offset === 0}
                    onClick={() => updateParams({ offset: Math.max(0, offset - PAGE_SIZE) })}
                  >
                    Previous
                  </Button>
                  <Text size="sm" c="dimmed">Page {page} of {totalPages}</Text>
                  <Button
                    variant="default"
                    size="xs"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => updateParams({ offset: offset + PAGE_SIZE })}
                  >
                    Next
                  </Button>
                </Group>
              </>
            )}
              </Stack>
            </Paper>
          </Stack>

    </PageShell>
  );
}
